export type NoteType = 'footnote' | 'endnote' | 'citation';

export interface Note {
  id: string;
  type: NoteType;
  content: string;
  number?: number;
  position: { start: number; end: number };
}

export interface FootnotePlacement {
  noteId: string;
  pageNumber: number;
  y: number;
}

export class NotesCollection {
  private notes = new Map<string, Note>();
  private order: Note[] = [];
  private footnoteCounter = 0;
  private endnoteCounter = 0;
  private citationCounter = 0;

  addNote(note: Note): void {
    if (this.notes.has(note.id)) {
      return;
    }

    this.notes.set(note.id, note);
    this.order.push(note);

    switch (note.type) {
      case 'footnote':
        note.number = ++this.footnoteCounter;
        break;
      case 'endnote':
        note.number = ++this.endnoteCounter;
        break;
      case 'citation':
        note.number = ++this.citationCounter;
        break;
    }
  }

  getNote(id: string): Note | undefined {
    return this.notes.get(id);
  }

  getAllNotes(): Note[] {
    return [...this.order];
  }

  getFootnotes(): Note[] {
    return this.order.filter((n) => n.type === 'footnote');
  }

  getEndnotes(): Note[] {
    return this.order.filter((n) => n.type === 'endnote');
  }

  getCitations(): Note[] {
    return this.order.filter((n) => n.type === 'citation');
  }

  clear(): void {
    this.notes.clear();
    this.order = [];
    this.footnoteCounter = 0;
    this.endnoteCounter = 0;
    this.citationCounter = 0;
  }
}

// Support custom footnote syntax: [^:content]
const NOTE_REGEX = /\[\^:([^\]]+)\]/g;
const ENDNOTE_REGEX = /\[~:([^\]]+)\]/g;
const CITATION_REGEX = /\[@(\w+):([^\]]+)\]/g;

export function parseNotes(content: string): NotesCollection {
  const collection = new NotesCollection();

  let match;

  // Parse custom footnotes: [^:content]
  NOTE_REGEX.lastIndex = 0;
  while ((match = NOTE_REGEX.exec(content)) !== null) {
    collection.addNote({
      id: `fn-${collection.getAllNotes().length + 1}`,
      type: 'footnote',
      content: match[1],
      position: { start: match.index, end: match.index + match[0].length },
    });
  }

  ENDNOTE_REGEX.lastIndex = 0;
  while ((match = ENDNOTE_REGEX.exec(content)) !== null) {
    collection.addNote({
      id: `en-${collection.getAllNotes().length + 1}`,
      type: 'endnote',
      content: match[1],
      position: { start: match.index, end: match.index + match[0].length },
    });
  }

  CITATION_REGEX.lastIndex = 0;
  while ((match = CITATION_REGEX.exec(content)) !== null) {
    collection.addNote({
      id: match[1],
      type: 'citation',
      content: match[2],
      position: { start: match.index, end: match.index + match[0].length },
    });
  }

  return collection;
}

export function renderFootnoteMarker(number: number): string {
  return String(number);
}

export function renderEndnoteMarker(number: number): string {
  return String(number);
}

export function renderCitationMarker(id: string, number?: number): string {
  return number !== undefined ? `[${number}]` : `[@${id}]`;
}
